'use client';

import { Card, MedRMStatus, ProfileCardHeader } from 'src/components';
import { useProfile } from 'src/context/providers/ProfileProvider';
import React, { useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { useGetMappingsQuery } from 'src/api/redux';
import { Tabs, Tab, TabSet, TabPanel } from 'src/components/PulseUI';

import { entityTypeSlugMap } from 'src/components/Settings/constants';
import { CRMObject, CRMMapping } from './components/types';
import CRMTabContent from './components/CRMTabContent';
import CRMPushButton from './components/CRMPushButton';
import DeleteCRMLinkDialog from './components/DeleteCRMLinkDialog';

// Main Component
const ProfileCRMDetails: React.FC = () => {
  const { entityDetails } = useProfile();
  const { type } = useParams<{ type: string }>();
  const [expandedTabs, setExpandedTabs] = useState<Record<string, boolean>>({});
  const [singleExpanded, setSingleExpanded] = useState(false);
  const [deleteCrmLink, setDeleteCrmLink] = useState<CRMObject | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const crmDetails = entityDetails?.crm_links as CRMObject[] | undefined;
  const hasLinks = crmDetails && crmDetails.length > 0;
  const linksLength = crmDetails?.length || 0;

  // CRM Type Formatting
  const enabledCrmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  const isThirdParty = enabledCrmTypes[0] === 'third_party';
  const isMedRM = enabledCrmTypes[0] === 'medrm';

  const formattedCrmName = useMemo(() => {
    if (isMedRM) {
      return 'MedRM';
    }
    const crmTypeLowercase = enabledCrmTypes[0] ?? '';
    return crmTypeLowercase.charAt(0).toUpperCase() + crmTypeLowercase.slice(1);
  }, [enabledCrmTypes, isMedRM]);

  const header = isThirdParty
    ? 'Your CRM Details'
    : `${formattedCrmName} Details`;

  // Group CRM links by object type
  const sortedCrmLinksByType = useMemo(() => {
    if (!hasLinks) return {};

    return crmDetails.reduce((acc, link) => {
      const objectType = link.crm_object_type || 'unknown';
      if (!acc[objectType]) {
        acc[objectType] = [];
      }
      acc[objectType].push(link);
      return acc;
    }, {} as Record<string, CRMObject[]>);
  }, [crmDetails, hasLinks]);

  const objectTypes = useMemo(() => {
    return Object.keys(sortedCrmLinksByType);
  }, [sortedCrmLinksByType]);

  // Filter mappings by current object type
  const normalizedType = entityTypeSlugMap[type?.toLowerCase()] || type;
  const filteredMappings = useMemo(() => {
    return allMappings?.results?.filter(
      ({ medscout_object_type, enabled }) =>
        medscout_object_type?.toLowerCase() === normalizedType && enabled
    ) as CRMMapping[] | undefined;
  }, [allMappings?.results, normalizedType]);

  const pushToCRMEnabled = useMemo(() => {
    if (!filteredMappings) return false;
    return filteredMappings.length > 0;
  }, [filteredMappings]);

  // Check if there's another mapping available to push to
  const stillActive =
    hasLinks &&
    filteredMappings &&
    filteredMappings.length > 1 &&
    filteredMappings.length > linksLength;

  // Determine alternate mapping type
  const alternateMappingTypes = useMemo(() => {
    if (!hasLinks || !filteredMappings || filteredMappings.length <= 1)
      return undefined;

    const existingCrmType = crmDetails[0]?.crm;

    const alternateMappings = filteredMappings.filter(
      (mapping) =>
        mapping.crm === existingCrmType &&
        mapping.crm_object_type !== crmDetails[0]?.crm_object_type
    );

    return alternateMappings.map((mapping) => mapping.crm_object_type);
  }, [filteredMappings, crmDetails, hasLinks]);

  // Toggle expanded state for a specific tab
  const toggleTabExpanded = (type: string) => {
    setExpandedTabs((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Handle delete CRM link
  const handleDeleteCrmLink = (crmLink: CRMObject) => {
    setDeleteCrmLink(crmLink);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeleteCrmLink(null);
  };

  // Early return if no data to display
  if (
    !entityDetails ||
    allMappings?.count < 1 ||
    (!hasLinks && !pushToCRMEnabled)
  )
    return null;

  return (
    <Card className="p-6 bg-white border border-neutral-200 rounded-lg w-full">
      <ProfileCardHeader
        icon={filteredMappings?.[0]?.crm?.toLowerCase() || 'salesforce'}
        title={header}
        isCRM={true}
      />
      <div className="flex flex-col gap-4 items-center">
        {/* Render Push to CRM button if no CRM links exist */}
        {!hasLinks && !isMedRM && <CRMPushButton />}
        {/* Render MedRM status if MedRM */}
        {isMedRM && <MedRMStatus provider={entityDetails} />}

        {/* With links: Render CRM tabs or single push details if not MedRM */}
        {!isMedRM && (
          <>
            {/* Multiple object types - render tabs */}
            {objectTypes.length > 1 && (
              <Tabs className="w-full pt-0 mt-0">
                {/* Wrap TabSet in a div with overflow handling for scrollability */}
                <div className="overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                  <TabSet
                    id="crm-tabs"
                    direction="horizontal"
                    variant="default"
                    className="w-max min-w-full flex items-center"
                  >
                    {objectTypes.map((type, index) => (
                      <Tab
                        key={type}
                        tabSetId="crm-tabs"
                        index={index}
                        className={`whitespace-nowrap text-center justify-center ${
                          objectTypes.length <= 3
                            ? 'flex-1' // For few tabs: fill available space
                            : 'px-4' // For many tabs: use fixed padding
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Tab>
                    ))}
                  </TabSet>
                </div>

                {objectTypes.map((type, index) => (
                  <TabPanel
                    key={type}
                    tabSetId="crm-tabs"
                    index={index}
                    className="mt-4"
                  >
                    <CRMTabContent
                      links={sortedCrmLinksByType[type]}
                      isExpanded={!!expandedTabs[type]}
                      toggleExpanded={() => toggleTabExpanded(type)}
                      onDelete={handleDeleteCrmLink}
                    />
                  </TabPanel>
                ))}
              </Tabs>
            )}

            {/* Single object type - no tabs needed */}
            {objectTypes.length === 1 && (
              <div className="w-full">
                {stillActive &&
                  alternateMappingTypes &&
                  alternateMappingTypes.length > 0 && (
                    <div className="pb-4">
                      <CRMPushButton
                        variant="secondary"
                        mappingTypes={alternateMappingTypes}
                      />
                    </div>
                  )}
                <div className="flex flex-col gap-2">
                  <CRMTabContent
                    links={sortedCrmLinksByType[objectTypes[0]]}
                    isExpanded={singleExpanded}
                    toggleExpanded={() => setSingleExpanded(!singleExpanded)}
                    onDelete={handleDeleteCrmLink}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <DeleteCRMLinkDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        crmLink={deleteCrmLink}
        onDeleteSuccess={handleCloseDeleteDialog}
      />
    </Card>
  );
};

export default ProfileCRMDetails;
